import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { MoneyInput } from '../../../components/MoneyInput';

export default function GoalsStep({ formData, updateFormData }) {
  const retirementGoals = formData.retirement || [];
  const collegeGoals = formData.college || [];
  const otherGoals = formData.otherGoals || [];
  const publicSchool = formData.publicSchool || [];
  const privateSchool = formData.privateSchool || [];

  const updateSection = (patch) => updateFormData({ ...formData, ...patch });

  const addRetirement = () => updateSection({ retirement: [...retirementGoals, { importance: '', ageToRetire: '', lifeExpectancy: '' }] });
  const removeRetirement = (idx) => updateSection({ retirement: retirementGoals.filter((_, i) => i !== idx) });
  const updateRetirement = (idx, field, value) => {
    const next = retirementGoals.map((g, i) => (i === idx ? { ...g, [field]: value } : g));
    updateSection({ retirement: next });
  };

  const addCollege = () => updateSection({ college: [...collegeGoals, { childName: '', yearToStart: '', yearsOfCollege: '', costEstimate: '', importance: '' }] });
  const removeCollege = (idx) => updateSection({ college: collegeGoals.filter((_, i) => i !== idx) });
  const updateCollege = (idx, field, value) => {
    const next = collegeGoals.map((g, i) => (i === idx ? { ...g, [field]: value } : g));
    updateSection({ college: next });
  };

  const addOther = () => updateSection({ otherGoals: [...otherGoals, { goal: '', year: '', amount: '' }] });
  const removeOther = (idx) => updateSection({ otherGoals: otherGoals.filter((_, i) => i !== idx) });
  const updateOther = (idx, field, value) => {
    const next = otherGoals.map((g, i) => (i === idx ? { ...g, [field]: value } : g));
    updateSection({ otherGoals: next });
  };

  const addPublicSchool = () => updateSection({ publicSchool: [...publicSchool, { forDependent: '', startYear: '', endYear: '', annualAmount: '' }] });
  const removePublicSchool = (idx) => updateSection({ publicSchool: publicSchool.filter((_, i) => i !== idx) });
  const updatePublicSchool = (idx, field, value) => {
    const next = publicSchool.map((g, i) => (i === idx ? { ...g, [field]: value } : g));
    updateSection({ publicSchool: next });
  };

  const addPrivateSchool = () => updateSection({ privateSchool: [...privateSchool, { forDependent: '', startYear: '', endYear: '', annualAmount: '' }] });
  const removePrivateSchool = (idx) => updateSection({ privateSchool: privateSchool.filter((_, i) => i !== idx) });
  const updatePrivateSchool = (idx, field, value) => {
    const next = privateSchool.map((g, i) => (i === idx ? { ...g, [field]: value } : g));
    updateSection({ privateSchool: next });
  };

  const renderSchoolSection = (title, list, addFn, removeFn, updateFn, testIdPrefix) => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-[#0f172a]">{title}</h3>
        <Button onClick={addFn} size="sm" variant="outline" data-testid={`add-${testIdPrefix}-button`}>
          <span className="text-lg mr-1">+</span> Add
        </Button>
      </div>
      {list.map((g, idx) => (
        <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-medium text-[#475569]">{title} #{idx + 1}</span>
            <Button onClick={() => removeFn(idx)} size="sm" variant="ghost" data-testid={`remove-${testIdPrefix}-${idx}`}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input placeholder="For (Dependent Name)" value={g.forDependent || ''} onChange={(e) => updateFn(idx, 'forDependent', e.target.value)} />
            <Input placeholder="Start Year" value={g.startYear || ''} onChange={(e) => updateFn(idx, 'startYear', e.target.value)} />
            <Input placeholder="End Year" value={g.endYear || ''} onChange={(e) => updateFn(idx, 'endYear', e.target.value)} />
            <MoneyInput placeholder="Annual Amount Needed" value={g.annualAmount} onChange={(v) => updateFn(idx, 'annualAmount', v)} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="goals-step">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Financial Goals
      </h2>

      {/* Retirement */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Retirement Goals</h3>
          <Button onClick={addRetirement} size="sm" variant="outline" data-testid="add-retirement-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        {retirementGoals.map((goal, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Retirement Goal #{idx + 1}</span>
              <Button onClick={() => removeRetirement(idx)} size="sm" variant="ghost" data-testid={`remove-retirement-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input placeholder="Importance (1-10)" value={goal.importance || ''} onChange={(e) => updateRetirement(idx, 'importance', e.target.value)} />
              <Input placeholder="Age to Retire" value={goal.ageToRetire || ''} onChange={(e) => updateRetirement(idx, 'ageToRetire', e.target.value)} />
              <Input placeholder="Life Expectancy" value={goal.lifeExpectancy || ''} onChange={(e) => updateRetirement(idx, 'lifeExpectancy', e.target.value)} />
            </div>
          </div>
        ))}
      </div>

      {/* College */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">College Goals</h3>
          <Button onClick={addCollege} size="sm" variant="outline" data-testid="add-college-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        {collegeGoals.map((goal, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">College Goal #{idx + 1}</span>
              <Button onClick={() => removeCollege(idx)} size="sm" variant="ghost" data-testid={`remove-college-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Child's Name" value={goal.childName || ''} onChange={(e) => updateCollege(idx, 'childName', e.target.value)} />
              <Input placeholder="Year to Start" value={goal.yearToStart || ''} onChange={(e) => updateCollege(idx, 'yearToStart', e.target.value)} />
              <Input placeholder="Years of College" value={goal.yearsOfCollege || ''} onChange={(e) => updateCollege(idx, 'yearsOfCollege', e.target.value)} />
              <MoneyInput placeholder="Cost Estimate" value={goal.costEstimate} onChange={(v) => updateCollege(idx, 'costEstimate', v)} />
              <Input placeholder="Importance (1-10)" value={goal.importance || ''} onChange={(e) => updateCollege(idx, 'importance', e.target.value)} />
            </div>
          </div>
        ))}
      </div>

      {/* Other Financial Goals */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Other Financial Goals</h3>
          <Button onClick={addOther} size="sm" variant="outline" data-testid="add-other-goal-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        {otherGoals.map((g, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Other Goal #{idx + 1}</span>
              <Button onClick={() => removeOther(idx)} size="sm" variant="ghost" data-testid={`remove-other-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input placeholder="Goal" value={g.goal || ''} onChange={(e) => updateOther(idx, 'goal', e.target.value)} />
              <Input placeholder="Year" value={g.year || ''} onChange={(e) => updateOther(idx, 'year', e.target.value)} />
              <MoneyInput placeholder="Amount Needed" value={g.amount} onChange={(v) => updateOther(idx, 'amount', v)} />
            </div>
          </div>
        ))}
      </div>

      {renderSchoolSection('Public School', publicSchool, addPublicSchool, removePublicSchool, updatePublicSchool, 'public-school')}
      {renderSchoolSection('Private School', privateSchool, addPrivateSchool, removePrivateSchool, updatePrivateSchool, 'private-school')}
    </div>
  );
}
